import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { XMarkIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { getFrameworkMasterListApi, getFrameworkMasterControlsApi } from '../../services/axios/AuthApis/TrustCenterApis';
import ApiLoader from '../ui/ApiLoader';
import { DOCUMENT_LIBRARY_CATEGORIES, DEPARTMENTS_WITH_DESIGNATIONS } from '../../constants/AppConstants';
import { FORM_INPUT_CLASS, FORM_LABEL_CLASS, FORM_SELECT_CLASS, FORM_TEXTAREA_CLASS } from '../ui/formStyles';

/* ── Constants ── */
const REVIEW_FREQUENCIES = ['Monthly', 'Quarterly', 'Semi-Annually', 'Annually', 'As Needed'];
const APP_PRIMARY_COLOR = 'var(--app-primary-color, #26256b)';

/* ────────────────────────────────────────────────────────────────────────────
   DocFormModal — Create / Edit a document + its linked controls.
   Props:
     record      – null for create, existing record object for edit
     onClose     – closes the modal
     onSave(payload) – called with form data; should return a promise
     loadFrameworks() – optional; defaults to the global FrameworkMaster list
                        (Platform Admin catalog). Pass a customer-scoped loader
                        to browse/link the caller's OWN controls instead
                        (Customer Admin) - the modal itself never decides scope.
     loadControls(frameworkId) – optional; pairs with loadFrameworks.
   ──────────────────────────────────────────────────────────────────────────── */
const defaultLoadFrameworks = async () => {
  const res = await getFrameworkMasterListApi();
  return res?.frameworks || (Array.isArray(res) ? res : []);
};
const defaultLoadControls = async (fwId) => {
  const data = await getFrameworkMasterControlsApi(fwId, 1, 500, '');
  return data.controls || data.results || [];
};

const DocFormModal = ({ record, onClose, onSave, loadFrameworks = defaultLoadFrameworks, loadControls: loadControlsProp = defaultLoadControls }) => {
  const isEdit = !!record;

  /* ── Framework list (loaded internally) ── */
  const [frameworks, setFrameworks] = useState([]);
  const [frameworksLoading, setFrameworksLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setFrameworksLoading(true);
      try {
        const list = await loadFrameworks();
        if (!cancelled) setFrameworks(Array.isArray(list) ? list : []);
      } catch { /* ignore */ }
      finally { if (!cancelled) setFrameworksLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [loadFrameworks]);

  /* ── form state ── */
  const [form, setForm] = useState({
    policy_procedure_guideline: record?.policy_procedure_guideline || '',
    control_domain: record?.control_domain || '',

    category: record?.category || '',
    owner: record?.owner ? record.owner.split(' / ') : [],
    review_frequency: record?.review_frequency || '',
    control_description_objective: record?.control_description_objective || '',
    purpose_coverage: record?.purpose_coverage || '',
    sample_implementation_evidence: record?.sample_implementation_evidence || '',
    serial_no: record?.serial_no || '',
    control_ids: record?.control_ids || [],
    show_to_employees: record?.show_to_employees || false,
    framework_ids: record?.framework_ids || [],
  });
  const [saving, setSaving] = useState(false);

  /* ── controls loaded for the selected framework ── */
  const [controls, setControls] = useState([]);       // { id, control_id, title, category }
  const [controlsLoading, setControlsLoading] = useState(false);
  const [controlSearch, setControlSearch] = useState('');
  const [browseFrameworkId, setBrowseFrameworkId] = useState(record?.control_framework_ids?.[0] || '');

  // Seed controlMetaMap from record.control_labels so edit mode shows names immediately
  const [controlMetaMap, setControlMetaMap] = useState(() => {
    const map = {};
    if (record?.control_ids && record?.control_labels) {
      record.control_ids.forEach((cid, i) => {
        const label = record.control_labels[i] || '';
        const fwName = record?.control_frameworks?.[i] || '';
        const fwId = record?.control_framework_ids?.[i] || '';
        // label format: "APO01 — Manage the IT Management..."
        const sepIdx = label.indexOf(' — ');
        const control_id = sepIdx > -1 ? label.substring(0, sepIdx) : label;
        const title = sepIdx > -1 ? label.substring(sepIdx + 3) : '';
        map[cid] = { control_id, title, category: '', framework_name: fwName, framework_id: fwId };
      });
    }
    return map;
  });

  /* Fetch controls whenever browseFrameworkId changes */
  const loadControls = useCallback(async (fwId) => {
    if (!fwId) { setControls([]); return; }
    setControlsLoading(true);
    try {
      const list = await loadControlsProp(fwId);
      setControls(Array.isArray(list) ? list : []);
      // Cache metadata so previously selected controls keep their labels
      const fwObj = frameworks.find((fw) => fw.id === fwId);
      const fwName = fwObj?.name || '';
      setControlMetaMap((prev) => {
        const next = { ...prev };
        list.forEach((c) => { next[c.id] = { control_id: c.control_id, title: c.title, category: c.category, framework_name: fwName, framework_id: fwId }; });
        return next;
      });
    } catch {
      setControls([]);
    } finally {
      setControlsLoading(false);
    }
  }, [frameworks, loadControlsProp]);

  useEffect(() => {
    loadControls(browseFrameworkId);
  }, [browseFrameworkId, loadControls]);

  /* ── helpers ── */
  const handleChange = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const toggleControl = (ctrlId) => {
    setForm((f) => {
      const ids = f.control_ids.includes(ctrlId)
        ? f.control_ids.filter((id) => id !== ctrlId)
        : [...f.control_ids, ctrlId];
      return { ...f, control_ids: ids };
    });
  };

  const selectAllFiltered = () => {
    const filtered = filteredControls.map((c) => c.id);
    setForm((f) => {
      const merged = [...new Set([...f.control_ids, ...filtered])];
      return { ...f, control_ids: merged };
    });
  };

  const deselectAllFiltered = () => {
    const filtered = new Set(filteredControls.map((c) => c.id));
    setForm((f) => ({
      ...f,
      control_ids: f.control_ids.filter((id) => !filtered.has(id)),
    }));
  };

  const filteredControls = controls.filter((c) => {
    if (!controlSearch.trim()) return true;
    const q = controlSearch.toLowerCase();
    return (
      (c.control_id || '').toLowerCase().includes(q) ||
      (c.title || '').toLowerCase().includes(q) ||
      (c.category || '').toLowerCase().includes(q)
    );
  });

  /* Derive control_domain automatically from selected controls */
  const derivedControlDomain = (() => {
    const domains = form.control_ids
      .map((cid) => {
        const c = controls.find((x) => x.id === cid) || controlMetaMap[cid];
        return c?.category || '';
      })
      .filter(Boolean);
    return [...new Set(domains)].join(', ');
  })();

  /* Derive framework_ids automatically from selected controls */
  const derivedFrameworkIds = (() => {
    const ids = new Set();
    form.control_ids.forEach((cid) => {
      const c = controls.find((x) => x.id === cid) || controlMetaMap[cid];
      const fwId = c?.framework_id;
      if (fwId) ids.add(fwId);
    });
    return [...ids];
  })();

  /* ── Owner helpers (multi-select from departments/roles) ── */
  const [ownerSearch, setOwnerSearch] = useState('');

  const allOwnerRoles = DEPARTMENTS_WITH_DESIGNATIONS.flatMap((dept) =>
    dept.roles.map((r) => ({ key: r.role, role: r.role, department: dept.name, responsibility: r.responsibility }))
  );

  const filteredOwnerRoles = allOwnerRoles.filter((r) => {
    if (!ownerSearch.trim()) return true;
    const q = ownerSearch.toLowerCase();
    return r.role.toLowerCase().includes(q) || r.department.toLowerCase().includes(q);
  });

  const toggleOwner = (role) => {
    setForm((f) => {
      const owners = f.owner.includes(role)
        ? f.owner.filter((o) => o !== role)
        : [...f.owner, role];
      return { ...f, owner: owners };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.policy_procedure_guideline.trim()) return;
    setSaving(true);
    try {
      await onSave({ ...form, framework_ids: derivedFrameworkIds, control_domain: derivedControlDomain, owner: form.owner.join(' / ') });
    } finally {
      setSaving(false);
    }
  };

  /* ── style tokens ── */
  const inputCls = FORM_INPUT_CLASS;
  const selectCls = FORM_SELECT_CLASS;
  const textAreaCls = FORM_TEXTAREA_CLASS;
  const labelCls = FORM_LABEL_CLASS;

  const modalContent = (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[92vh] flex flex-col overflow-hidden">
        {/* ── Header ── */}
        <div
          className="px-6 py-4 flex items-center justify-between shrink-0"
          style={{
            background: `linear-gradient(90deg, color-mix(in srgb, ${APP_PRIMARY_COLOR} 88%, black 12%), ${APP_PRIMARY_COLOR})`,
          }}
        >
          <h3 className="text-white font-bold text-lg">
            {isEdit ? 'Edit Document' : 'Add New Document'}
          </h3>
          <button onClick={onClose} className="text-white/80 hover:text-white">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        {/* ── Loading overlay ── */}
        {(frameworksLoading || saving) && (
          <div className="flex-1 flex items-center justify-center py-16">
            <ApiLoader label={saving ? 'Saving…' : 'Loading…'} />
          </div>
        )}

        {/* ── Form body ── */}
        {!frameworksLoading && !saving && (
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-5">
          {/* Row 1 – Title */}
          <div>
            <label className={labelCls}>
              Title <span className="text-red-500">*</span>
            </label>
            <input
              className={inputCls}
              value={form.policy_procedure_guideline}
              onChange={handleChange('policy_procedure_guideline')}
              required
            />
          </div>

          {/* ── Control multi-select ── */}
          <div>
            <label className={labelCls}>
              Map Controls{' '}
              <span className="text-gray-400 font-normal">
                ({form.control_ids.length} selected)
              </span>
            </label>

            {/* Browse Controls From — framework selector */}
            <div className="mb-2">
              <select
                className={`${selectCls} text-xs`}
                value={browseFrameworkId}
                onChange={(e) => {
                  setBrowseFrameworkId(e.target.value);
                  setControlSearch('');

                }}
              >
                <option value="">Browse controls from…</option>
                {(frameworks || []).map((fw) => (
                  <option key={fw.id} value={fw.id}>
                    {fw.name}
                  </option>
                ))}
              </select>
            </div>

            {!browseFrameworkId ? (
              <p className="text-xs text-gray-400 py-2">
                Select a framework above to browse and pick controls.
              </p>
            ) : controlsLoading ? (
                <div className="flex items-center gap-2 text-xs text-gray-500 py-3">
                  <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                  </svg>
                  Loading controls…
                </div>
              ) : controls.length === 0 ? (
                <p className="text-xs text-gray-400 py-2">
                  No controls available for the selected framework.
                </p>
              ) : (
                <div className="border border-gray-300 rounded-lg overflow-hidden">
                  {/* Search & bulk actions */}
                  <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 border-b border-gray-200">
                    <MagnifyingGlassIcon className="w-4 h-4 text-gray-400 shrink-0" />
                    <input
                      type="text"
                      placeholder="Search controls…"
                      value={controlSearch}
                      onChange={(e) => setControlSearch(e.target.value)}
                      className="flex-1 text-xs bg-transparent outline-none"
                    />
                    <button
                      type="button"
                      onClick={selectAllFiltered}
                      className="text-[10px] hover:underline whitespace-nowrap"
                      style={{ color: APP_PRIMARY_COLOR }}
                    >
                      Select all
                    </button>
                    <span className="text-gray-300">|</span>
                    <button
                      type="button"
                      onClick={deselectAllFiltered}
                      className="text-[10px] text-red-500 hover:underline whitespace-nowrap"
                    >
                      Deselect all
                    </button>
                  </div>

                  {/* Scrollable list */}
                  <div className="max-h-48 overflow-y-auto divide-y divide-gray-100">
                    {filteredControls.length === 0 ? (
                      <p className="text-xs text-gray-400 py-3 text-center">No matching controls.</p>
                    ) : (
                      filteredControls.map((c) => {
                        const checked = form.control_ids.includes(c.id);
                        return (
                          <label
                            key={c.id}
                            className={`flex items-start gap-2.5 px-3 py-2 cursor-pointer transition-colors ${
                              checked ? '' : ''
                            }`}
                            style={{
                              backgroundColor: checked
                                ? `color-mix(in srgb, ${APP_PRIMARY_COLOR} 10%, white)`
                                : 'transparent',
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => toggleControl(c.id)}
                              className="mt-0.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                            <div className="min-w-0">
                              <span className="text-xs font-semibold text-gray-800">
                                {c.control_id}
                              </span>
                              <span className="text-xs text-gray-500 ml-1.5">— {c.title}</span>
                              {c.category && (
                                <span className="ml-2 text-[10px] bg-gray-500 text-white px-1.5 py-0.5 rounded">
                                  {c.category}
                                </span>
                              )}
                            </div>
                          </label>
                        );
                      })
                    )}
                  </div>
                </div>
              )}
            </div>

          {/* ── Selected Controls Summary ── */}
          {form.control_ids.length > 0 && (
            <div>
              <label className={labelCls}>
                Selected Controls{' '}
                <span className="text-gray-400 font-normal">({form.control_ids.length})</span>
              </label>
              <div className="flex flex-wrap gap-1.5">
                {form.control_ids.map((cid) => {
                  const ctrl = controls.find((c) => c.id === cid) || controlMetaMap[cid];
                  const fwName = ctrl?.framework_name;
                  return (
                    <span
                      key={cid}
                      className="inline-flex items-center gap-1 text-xs border rounded-full px-2.5 py-1"
                      style={{
                        backgroundColor: `color-mix(in srgb, ${APP_PRIMARY_COLOR} 10%, white)`,
                        borderColor: `color-mix(in srgb, ${APP_PRIMARY_COLOR} 22%, white)`,
                        color: APP_PRIMARY_COLOR,
                      }}
                    >
                      {fwName && (
                        <span
                          className="text-[9px] text-white px-1.5 py-0.5 rounded-full mr-0.5 max-w-[80px] truncate"
                          style={{ backgroundColor: APP_PRIMARY_COLOR }}
                          title={fwName}
                        >
                          {fwName}
                        </span>
                      )}
                      <span className="font-semibold">{ctrl?.control_id || cid}</span>
                      {ctrl?.title && (
                        <span className="max-w-[140px] truncate" style={{ color: `color-mix(in srgb, ${APP_PRIMARY_COLOR} 80%, black 20%)` }}>— {ctrl.title}</span>
                      )}
                      <button
                        type="button"
                        onClick={() => toggleControl(cid)}
                        className="ml-0.5 hover:text-red-500 transition-colors"
                        style={{ color: `color-mix(in srgb, ${APP_PRIMARY_COLOR} 65%, white)` }}
                      >
                        <XMarkIcon className="w-3.5 h-3.5" />
                      </button>
                    </span>
                  );
                })}
              </div>
            </div>
          )}

          {/* Row 2 */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Control Domain</label>
              <div className={`${inputCls} bg-gray-50 text-gray-600 min-h-[38px]`}>
                {derivedControlDomain || <span className="text-gray-400 italic">Auto-filled from selected controls</span>}
              </div>
            </div>
            <div>
              <label className={labelCls}>Category</label>
              <select className={selectCls} value={form.category} onChange={handleChange('category')}>
                <option value="">Select Category</option>
                {DOCUMENT_LIBRARY_CATEGORIES.map((cat) => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Row 3 */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Review Frequency</label>
              <select className={selectCls} value={form.review_frequency} onChange={handleChange('review_frequency')}>
                <option value="">Select</option>
                {REVIEW_FREQUENCIES.map((f) => (
                  <option key={f} value={f}>{f}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelCls}>Serial No</label>
              <input
                type="number"
                className={inputCls}
                value={form.serial_no}
                onChange={handleChange('serial_no')}
              />
            </div>
          </div>

          {/* ── Owner multi-select ── */}
          <div>
            <label className={labelCls}>
              Owner{' '}
              <span className="text-gray-400 font-normal">({form.owner.length} selected)</span>
            </label>

            {/* Selected owners chips */}
            {form.owner.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-2">
                {form.owner.map((role) => (
                  <span
                    key={role}
                    className="inline-flex items-center gap-1 text-xs border rounded-full px-2.5 py-1"
                    style={{
                      backgroundColor: `color-mix(in srgb, ${APP_PRIMARY_COLOR} 10%, white)`,
                      borderColor: `color-mix(in srgb, ${APP_PRIMARY_COLOR} 22%, white)`,
                      color: APP_PRIMARY_COLOR,
                    }}
                  >
                    <span className="font-semibold">{role}</span>
                    <button
                      type="button"
                      onClick={() => toggleOwner(role)}
                      className="ml-0.5 hover:text-red-500 transition-colors"
                      style={{ color: `color-mix(in srgb, ${APP_PRIMARY_COLOR} 65%, white)` }}
                    >
                      <XMarkIcon className="w-3.5 h-3.5" />
                    </button>
                  </span>
                ))}
              </div>
            )}

            <div className="border border-gray-300 rounded-lg overflow-hidden">
              {/* Search */}
              <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 border-b border-gray-200">
                <MagnifyingGlassIcon className="w-4 h-4 text-gray-400 shrink-0" />
                <input
                  type="text"
                  placeholder="Search departments or roles…"
                  value={ownerSearch}
                  onChange={(e) => setOwnerSearch(e.target.value)}
                  className="flex-1 text-xs bg-transparent outline-none"
                />
              </div>
              {/* Scrollable list */}
              <div className="max-h-40 overflow-y-auto divide-y divide-gray-100">
                {filteredOwnerRoles.length === 0 ? (
                  <p className="text-xs text-gray-400 py-3 text-center">No matching roles.</p>
                ) : (
                  filteredOwnerRoles.map((r) => {
                    const checked = form.owner.includes(r.role);
                    return (
                      <label
                        key={`${r.department}-${r.role}`}
                        className={`flex items-start gap-2.5 px-3 py-2 cursor-pointer transition-colors ${
                          checked ? '' : ''
                        }`}
                        style={{
                          backgroundColor: checked
                            ? `color-mix(in srgb, ${APP_PRIMARY_COLOR} 10%, white)`
                            : 'transparent',
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleOwner(r.role)}
                          className="mt-0.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <div className="min-w-0">
                          <span className="text-xs font-semibold text-gray-800">{r.role}</span>
                          <span className="ml-2 text-[10px] bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">
                            {r.department}
                          </span>
                        </div>
                      </label>
                    );
                  })
                )}
              </div>
            </div>
          </div>

          {/* Row 4 – textareas */}
          <div>
            <label className={labelCls}>Control Description / Objective</label>
            <textarea
              rows={2}
              className={textAreaCls}
              value={form.control_description_objective}
              onChange={handleChange('control_description_objective')}
            />
          </div>
          <div>
            <label className={labelCls}>Purpose / Coverage</label>
            <textarea
              rows={2}
              className={textAreaCls}
              value={form.purpose_coverage}
              onChange={handleChange('purpose_coverage')}
            />
          </div>
           <div className="flex items-center justify-between px-4 py-3 bg-gray-50 rounded-xl border">
              <div>
                <p className="text-sm font-medium">Show to Employee</p>
                <p className="text-xs text-gray-500">
                  {form.show_to_employees
                    ? 'Visible to employees'
                    : 'Hidden from employees'}
                </p>
              </div>

              <button
                type="button"
                role="switch"
                aria-checked={form.show_to_employees}
                onClick={() =>
                  setForm((prev) => ({
                    ...prev,
                    show_to_employees: !prev.show_to_employees  ,
                  }))
                }
                className={`relative inline-flex h-6 w-11 rounded-full transition-colors ${
                  form.show_to_employees ? '' : 'bg-gray-300'
                }`}
                style={form.show_to_employees ? { backgroundColor: APP_PRIMARY_COLOR } : undefined}
              >
                <span
                  className={`inline-block h-5 w-5 bg-white rounded-full transform ${
                    form.show_to_employees ? 'translate-x-5' : 'translate-x-0'
                  }`}
                />
              </button>
            </div>

          <div>
            <label className={labelCls}>Sample Implementation Evidence</label>
            <textarea
              rows={2}
              className={textAreaCls}
              value={form.sample_implementation_evidence}
              onChange={handleChange('sample_implementation_evidence')}
            />
          </div>
        </form>
        )}

        {/* ── Footer ── */}
        {!frameworksLoading && !saving && (
        <div className="border-t border-gray-200 px-6 py-4 flex items-center justify-end gap-3 shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving || !form.policy_procedure_guideline.trim()}
            onClick={handleSubmit}
            className="px-5 py-2 text-sm font-medium text-white rounded-lg hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
            style={{ backgroundColor: APP_PRIMARY_COLOR }}
          >
            {saving && (
              <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
              </svg>
            )}
            {isEdit ? 'Update' : 'Create'}
          </button>
        </div>
        )}
      </div>
    </div>
  );

  if (typeof document === 'undefined') return null;
  return createPortal(modalContent, document.body);
};

export default DocFormModal;
